import React, { useState } from 'react';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { Printer } from 'lucide-react';

const CaseLabelPrinter = ({ item, onClick }) => {
    const [wineDetails, setWineDetails] = useState(null);
    const [loading, setLoading] = useState(false);
  
    const loadWineDetails = async () => {
      if (!item.wineID) return;
      setLoading(true);
      try {
        const details = await axios.get(`/api/wines/${item.wineID}`);
        setWineDetails(details.data);
      } catch (err) {
        console.error('Error loading wine details:', err);
      } finally {
        setLoading(false);
      }
    };
  
    const handleClick = async (e) => {
      e.stopPropagation();
      if (onClick) onClick(e);
      
      // Load wine details if not already loaded
      if (!wineDetails) {
        await loadWineDetails();
      }
      
      generateLabel();
    };
  
    const generateLabel = () => {
      // Create new jsPDF instance
      const doc = new jsPDF({
        orientation: 'landscape', 
        unit: 'in',
        format: [3, 4] // Height, Width
      });
  
      // Use merged data from both sources
      const wineData = {...item, ...wineDetails};

    // Set font styles
    doc.setFont('helvetica', 'bold');
    
    // First line: "Jeremy Wine Co."
    doc.setFontSize(14);
    doc.text('Jeremy Wine Co.', 2, 0.5, { align: 'center' });
    
    // Second line: Vintage (larger font)
    doc.setFontSize(22);
    const vintage = wineData.vintage || 'NV';
    doc.text(vintage.toString(), 2, 0.9, { align: 'center' });
    
    // Third line: Varietal or Fanciful Name (large font, all caps)
    doc.setFontSize(24);
    const mainText = wineData.fancifulName || wineData.varietal || 'WINE';
    const mainTextUpper = mainText.toUpperCase();
    
    // Calculate font size to fit width
    let fontSize = 24;
    let textWidth = doc.getStringUnitWidth(mainTextUpper) * fontSize / doc.internal.scaleFactor;
    
    while (textWidth > 3.5 && fontSize > 10) {
      fontSize -= 2;
      doc.setFontSize(fontSize);
      textWidth = doc.getStringUnitWidth(mainTextUpper) * fontSize / doc.internal.scaleFactor;
    }
    
    doc.text(mainTextUpper, 2, 1.4, { align: 'center' });
    
    // Fourth line: Vineyard or Version (smaller font)
    doc.setFontSize(12);
    const fourthLine = wineData.version || wineData.vineyard || '';
    doc.text(fourthLine, 2, 1.8, { align: 'center' });
    
    // Fifth line: Wine Style • Appellation
    const wineStyle = wineData.proWineType || wineData.varietalDetail || '';
    const appellation = wineData.appellation || '';
    const fifthLine = wineStyle && appellation 
      ? `${wineStyle} • ${appellation}`
      : wineStyle || appellation;
    
    doc.text(fifthLine, 2, 2.2, { align: 'center' });
    
    // Sixth line: BWN-16742, Bottling Date, Alc. Final Alcohol
    doc.setFontSize(10);
    const bottlingDate = wineData.bottlingDate ? new Date(wineData.bottlingDate).toLocaleDateString() : '';
    const alcohol = wineData.finalAlc ? `${wineData.finalAlc}%` : '';
    const sixthLine = `BWN-16742     ${bottlingDate}     Alc. ${alcohol}`;
    doc.text(sixthLine, 2, 2.6, { align: 'center' });
    
    // Save PDF with name based on wine details
    const fileName = `${vintage}_${mainText}_CaseLabel.pdf`;
    doc.save(fileName);
  };

  return (
    <button
      onClick={handleClick}
      className="btn btn-sm btn-outline-secondary"
      disabled={loading}
      title="Print Case Labels"
    >
      <Printer size={16} className="me-1" />
      {loading ? 'Loading...' : 'Case Label'}
    </button>
  );
};

export default CaseLabelPrinter;